/**
 * Referral Tracking utilities.
 *
 * Referral codes are passed via URL: ?ref=ABCD1234
 * The code is stored in localStorage for up to REFERRAL_EXPIRY_DAYS days (default: 30).
 * On signup, the code is read and saved to user.protectedData.referredBy.
 *
 * Usage:
 *   // In app.js (on mount):
 *   captureReferral();
 *
 *   // In SignupForm (before submit):
 *   const referredBy = getStoredReferral();
 *   // Include in protectedData: { referredBy }
 */

const STORAGE_KEY = 'ith_referral_code';
const EXPIRY_KEY = 'ith_referral_expiry';
const EXPIRY_DAYS = parseInt(process.env.REACT_APP_REFERRAL_EXPIRY_DAYS || '30', 10);

/**
 * Check URL for ?ref= parameter and store in localStorage.
 * Call this once on app mount (e.g., in app.js).
 */
export const captureReferral = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (!ref || !ref.match(/^[a-zA-Z0-9_-]{3,32}$/)) return;

  try {
    const expiresAt = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, ref);
    localStorage.setItem(EXPIRY_KEY, String(expiresAt));
  } catch (_) {}
};

/**
 * Get stored referral code, or null if expired/not set.
 */
export const getStoredReferral = () => {
  if (typeof window === 'undefined') return null;
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    const expiry = parseInt(localStorage.getItem(EXPIRY_KEY) || '0', 10);
    if (!code || Date.now() > expiry) return null;
    return code;
  } catch (_) {
    return null;
  }
};

/**
 * Clear stored referral code (after it has been used on signup).
 */
export const clearStoredReferral = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch (_) {}
};

/**
 * Generate a referral code from a user ID or display name.
 * @param {string} userId — Sharetribe user UUID
 * @returns {string} — short alphanumeric code
 */
export const generateReferralCode = userId => {
  if (!userId) return '';
  // Use first 8 chars of UUID (without hyphens) + prefix
  return userId.replace(/-/g, '').substring(0, 8).toUpperCase();
};

/**
 * Build a referral URL.
 * @param {string} baseUrl — e.g. 'https://example.com'
 * @param {string} code — referral code
 */
export const buildReferralUrl = (baseUrl, code) => {
  const url = new URL(baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
  url.searchParams.set('ref', code);
  return url.toString();
};
